import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Save, X, AlertTriangle, Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";

const STATUS = ["Entwurf", "Freigabe fehlt", "Freigegeben", "Veröffentlicht", "Archiviert"];
const empty = {
  text: "",
  author: "",
  source: "",
  review_date: "",
  stars: 5,
  consent_written: false,
  consent_website: false,
  show_on_home: false,
  status: "Entwurf",
  order: 0,
};

export default function Reviews() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reviews");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!editing.text?.trim()) return toast.error("Bitte einen Bewertungstext angeben.");
    setSaving(true);
    try {
      const payload = { ...editing };
      delete payload.id;
      delete payload.is_sample;
      delete payload.sample_index;
      delete payload.created_at;
      if (payload.stars === "" || payload.stars === null) payload.stars = null;
      else payload.stars = Number(payload.stars);
      if (!payload.review_date) payload.review_date = null;
      if (editing.id) await api.patch(`/admin/reviews/${editing.id}`, payload);
      else await api.post("/admin/reviews", payload);
      toast.success("Bewertung gespeichert.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (r) => {
    if (r.is_sample) return toast.error("Mustertexte können nicht gelöscht werden.");
    if (!window.confirm("Bewertung löschen?")) return;
    try {
      await api.delete(`/admin/reviews/${r.id}`);
      toast.success("Gelöscht.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const inputCls =
    "mt-1 w-full rounded-sm border border-light-gray bg-light-gray px-3 py-2.5 text-sm outline-none focus:border-yellow";

  const samples = items.filter((r) => r.is_sample);
  const real = items.filter((r) => !r.is_sample);

  return (
    <div data-testid="admin-reviews">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-anthracite">Kundenbewertungen</h1>
          <p className="mt-1 text-mid-gray">
            Nur echte, freigegebene und veröffentlichte Bewertungen erscheinen öffentlich.
          </p>
        </div>
        <button
          onClick={() => setEditing({ ...empty })}
          className="inline-flex items-center gap-2 rounded-sm bg-yellow px-4 py-2.5 font-heading text-sm font-bold text-brand-black hover:bg-yellow-dark"
          data-testid="review-new"
        >
          <Plus className="h-4 w-4" /> Neue Bewertung
        </button>
      </div>

      {/* Warnung Mustertexte */}
      {samples.length > 0 && (
        <div
          className="mt-6 flex items-start gap-3 rounded-sm border border-yellow/50 bg-yellow/10 p-4 text-sm text-anthracite"
          data-testid="review-sample-warning"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-yellow-dark" />
          <div>
            <p className="font-semibold">Mustertexte – nur intern.</p>
            <p className="mt-1 text-mid-gray">
              Die folgenden Beispieltexte sind ausschließlich als Vorlage sichtbar. Sie werden nie
              öffentlich angezeigt, nicht in Suchmaschinen indexiert und nicht in strukturierten
              Daten ausgeliefert.
            </p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-yellow" />
        </div>
      ) : (
        <>
          {/* Mustertexte */}
          {samples.length > 0 && (
            <section className="mt-6">
              <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-mid-gray">
                Mustertexte (intern)
              </h2>
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                {samples.map((r) => (
                  <div
                    key={r.id}
                    className="rounded-md border border-dashed border-mid-gray/40 bg-white p-4 text-sm"
                    data-testid={`review-sample-${r.id}`}
                  >
                    <span className="inline-block rounded-sm bg-anthracite px-2 py-0.5 text-xs font-bold uppercase tracking-widest text-yellow">
                      Mustertext
                    </span>
                    <p className="mt-3 italic text-mid-gray">„{r.text}“</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Echte Bewertungen */}
          <section className="mt-8">
            <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-mid-gray">
              Echte Bewertungen
            </h2>
            {real.length === 0 ? (
              <p
                className="mt-3 rounded-md border border-dashed border-mid-gray/30 bg-white p-10 text-center text-mid-gray"
                data-testid="reviews-empty"
              >
                Noch keine echten Bewertungen erfasst. Solange keine veröffentlicht ist, wird die
                Kundenstimmen-Sektion auf der Startseite komplett ausgeblendet.
              </p>
            ) : (
              <div className="mt-3 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {real.map((r) => (
                  <div
                    key={r.id}
                    className="rounded-md border border-light-gray bg-white p-4"
                    data-testid={`review-${r.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className={`rounded-sm px-2 py-1 text-xs font-semibold ${
                          r.status === "Veröffentlicht"
                            ? "bg-yellow text-brand-black"
                            : "bg-light-gray text-mid-gray"
                        }`}
                      >
                        {r.status}
                      </span>
                      {r.show_on_home && (
                        <span className="text-xs font-semibold text-anthracite">Startseite</span>
                      )}
                    </div>
                    {r.stars ? (
                      <div className="mt-3 flex gap-0.5" aria-label={`${r.stars} von 5 Sternen`}>
                        {Array.from({ length: 5 }).map((_, s) => (
                          <Star
                            key={s}
                            className={`h-4 w-4 ${
                              s < r.stars ? "fill-yellow text-yellow" : "text-light-gray"
                            }`}
                          />
                        ))}
                      </div>
                    ) : null}
                    <p className="mt-3 text-sm leading-relaxed text-anthracite">„{r.text}“</p>
                    <p className="mt-3 text-xs text-mid-gray">
                      {[r.author, r.source, r.review_date].filter(Boolean).join(" · ") || "—"}
                    </p>
                    <div className="mt-3 flex items-center justify-between">
                      <button
                        onClick={() => setEditing(r)}
                        className="text-sm font-bold text-anthracite hover:text-yellow-dark"
                        data-testid={`review-edit-${r.id}`}
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => del(r)}
                        className="text-red-500"
                        data-testid={`review-del-${r.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-black/60 p-4"
          data-testid="review-editor"
        >
          <div className="my-8 w-full max-w-2xl rounded-md bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold text-anthracite">
                {editing.id ? "Bewertung bearbeiten" : "Neue Bewertung"}
              </h2>
              <button onClick={() => setEditing(null)} aria-label="Schließen">
                <X className="h-5 w-5 text-mid-gray" />
              </button>
            </div>

            {editing.is_sample && (
              <p className="mt-3 rounded-sm border border-yellow/40 bg-yellow/10 p-3 text-xs text-anthracite">
                Mustertexte können nicht gespeichert werden.
              </p>
            )}

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="text-sm font-semibold text-anthracite sm:col-span-2">
                Bewertungstext *
                <textarea
                  value={editing.text}
                  onChange={(e) => setEditing((p) => ({ ...p, text: e.target.value }))}
                  className={inputCls}
                  rows={4}
                  data-testid="review-text"
                />
              </label>
              <label className="text-sm font-semibold text-anthracite">
                Autor / Name
                <input
                  value={editing.author || ""}
                  onChange={(e) => setEditing((p) => ({ ...p, author: e.target.value }))}
                  className={inputCls}
                  data-testid="review-author"
                />
              </label>
              <label className="text-sm font-semibold text-anthracite">
                Quelle (z. B. Google, persönlich)
                <input
                  value={editing.source || ""}
                  onChange={(e) => setEditing((p) => ({ ...p, source: e.target.value }))}
                  className={inputCls}
                  data-testid="review-source"
                />
              </label>
              <label className="text-sm font-semibold text-anthracite">
                Bewertungsdatum
                <input
                  type="date"
                  value={editing.review_date || ""}
                  onChange={(e) => setEditing((p) => ({ ...p, review_date: e.target.value }))}
                  className={inputCls}
                />
              </label>
              <label className="text-sm font-semibold text-anthracite">
                Sterne (1–5)
                <input
                  type="number"
                  min={1}
                  max={5}
                  value={editing.stars ?? ""}
                  onChange={(e) => setEditing((p) => ({ ...p, stars: e.target.value }))}
                  className={inputCls}
                  data-testid="review-stars"
                />
              </label>
              <label className="text-sm font-semibold text-anthracite">
                Status
                <select
                  value={editing.status}
                  onChange={(e) => setEditing((p) => ({ ...p, status: e.target.value }))}
                  className={inputCls}
                  data-testid="review-status"
                >
                  {STATUS.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-semibold text-anthracite">
                Reihenfolge
                <input
                  type="number"
                  value={editing.order || 0}
                  onChange={(e) => setEditing((p) => ({ ...p, order: Number(e.target.value) }))}
                  className={inputCls}
                />
              </label>
              <label className="flex items-center gap-2 text-sm text-anthracite sm:col-span-2">
                <input
                  type="checkbox"
                  checked={!!editing.consent_written}
                  onChange={(e) => setEditing((p) => ({ ...p, consent_written: e.target.checked }))}
                  className="accent-yellow"
                  data-testid="review-consent-written"
                />
                Schriftliche Freigabe des Kunden liegt vor
              </label>
              <label className="flex items-center gap-2 text-sm text-anthracite sm:col-span-2">
                <input
                  type="checkbox"
                  checked={!!editing.consent_website}
                  onChange={(e) => setEditing((p) => ({ ...p, consent_website: e.target.checked }))}
                  className="accent-yellow"
                  data-testid="review-consent-website"
                />
                Freigabe zur Veröffentlichung auf der Website
              </label>
              <label className="flex items-center gap-2 text-sm text-anthracite sm:col-span-2">
                <input
                  type="checkbox"
                  checked={!!editing.show_on_home}
                  onChange={(e) => setEditing((p) => ({ ...p, show_on_home: e.target.checked }))}
                  className="accent-yellow"
                  data-testid="review-show-home"
                />
                Auf der Startseite anzeigen
              </label>
            </div>

            <p className="mt-4 text-xs text-mid-gray">
              Hinweis: Der Status „Veröffentlicht“ ist nur möglich, wenn schriftliche und
              Website-Freigabe bestätigt sind.
            </p>

            <div className="mt-5 flex gap-3">
              <button
                onClick={save}
                disabled={saving || editing.is_sample}
                className="inline-flex items-center gap-2 rounded-sm bg-yellow px-5 py-3 font-heading font-bold text-brand-black hover:bg-yellow-dark disabled:opacity-60"
                data-testid="review-save"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Speichern
              </button>
              <button
                onClick={() => setEditing(null)}
                className="rounded-sm border border-light-gray px-5 py-3 font-heading font-semibold text-mid-gray hover:bg-light-gray"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
